import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { get } from 'lodash';

import GeneralError from 'components/GeneralError';
import { Button } from 'components/Button';
import Modal from 'components/Modal';
import { FormRow } from 'components/Form';
import StatementsTable from 'components/StatementsTable';
import ProgressLoading from 'components/ProgressLoading';
import useWorkflowGuard from 'hooks/useWorkflowGuard';
import { nextAction, backAction, checkinAction } from 'actions/workflow';
import { useConfig } from 'config/ConfigContext';
import { PROGRESS_BAR_DURATION_MS } from 'utils/constants';
import { hideTitleAndFooter, showTitleAndFooter } from 'utils/helper';

let bankWebSocket = null;
const NORMAL_CLOSE = 'Normal close';
const DELAY_POPUP_MS = 3 * 60 * 1000;

const ReviewBankAccounts = ({
  navigate,
  dispatch,
  searchParams,
}) => {
  useWorkflowGuard(searchParams, navigate);
  const { websocketUrl } = useConfig();
  const workflow = useSelector((state) => get(state, 'workflow'))
  const accounts = get(workflow, 'data.accounts') || [];
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generalError, setGeneralError] = useState(null);
  const applicationId = get(workflow, 'attributes.entityId');
  const [isLoadingBack, setIsLoadingBack] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showUnmatchedConditionsPopup, setShowUnmatchedConditionsPopup] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [showDelayPopup, setShowDelayPopup] = useState(false);
  const delayTimerRef = useRef(null);
  const hasProcessingAccounts = accounts.some((acc) => acc.processingStatus === 'InProgress');

  useEffect(() => {
    if (hasProcessingAccounts) {
      delayTimerRef.current = setTimeout(() => {
        setShowDelayPopup(true);
      }, DELAY_POPUP_MS);
    }

    return () => {
      if (delayTimerRef.current) {
        clearTimeout(delayTimerRef.current);
      }
    };
  }, [hasProcessingAccounts]);

  const fetchAccounts = () => {
    setIsLoading(true);

    dispatch(checkinAction({
      key: searchParams.get('key'),
      data: {
        checkStatus: true,
      },
      success: () => {
        setIsLoading(false);
      },
      fail: handleFail,
    }));
  };

  const listenBankStatementWebSocket = () => {
    bankWebSocket = new WebSocket(`${websocketUrl}?application_id=${applicationId}`);
    console.log('connection:', bankWebSocket);
    bankWebSocket.addEventListener('message', (event) => {
      console.log('eventData:', event.data);
      const eventData = JSON.parse(get(event, 'data'));
      const eventType = get(eventData, 'EventType');
      if (eventType === 'BANK_STATEMENT_PROCESSING_COMPLETE') {
        fetchAccounts();
      }
    });
    bankWebSocket.addEventListener('close', (event) => {
      if (get(event, 'reason') !== NORMAL_CLOSE || get(event, 'code') !== 1000) {
        console.log('Closed and Reconnecting:', event);
        listenBankStatementWebSocket();
      }
    });
  };

  useEffect(() => {
    if (applicationId) {
      listenBankStatementWebSocket();
    }

    return () => {
      bankWebSocket && bankWebSocket.close(1000, NORMAL_CLOSE);
      bankWebSocket = null;
    };
  }, [applicationId]);

  const handleFail = (err) => {
    const errorMessage = get(err, 'data.failure.errors.0.message');
    const errorSubject = get(err, 'data.failure.errors.0.subject');
    if (errorSubject === 'bankStatement') {
      setShowUnmatchedConditionsPopup(true);
    } else if (errorMessage) {
      setGeneralError(get(err, 'data.failure.errors.0'));
    }
    setIsSubmitting(false);
    setIsLoadingBack(false);
    setIsRedirecting(false);
    showTitleAndFooter()
  }

  const goToUploadBankStatements = () => {
    setIsRedirecting(true);

    dispatch(checkinAction({
      key: searchParams.get('key'),
      data: { redirectToUploadStatement: true },
      success: (res) => {
        setIsRedirecting(false);
        setShowUnmatchedConditionsPopup(false);
        const nextUrl = get(res, 'data.url');
        if (nextUrl) navigate(nextUrl);
      },
      fail: handleFail,
    }));
  };

  const handleLinkMore = () => {
    setIsLoadingBack(true);
    dispatch(backAction({
      key: searchParams.get('key'),
      data: {},
      success: (res) => {
        setIsLoadingBack(false);
        const nextUrl = get(res, 'data.url')
        if (nextUrl) navigate(nextUrl);
      },
      fail: handleFail,
    }));
  };

  const handleNext = () => {
    setIsSubmitting(true);
    hideTitleAndFooter();
    setProgress(0);

    const interval = setInterval(() => {
      setProgress(prev => prev < 90 ? prev + (90 / (PROGRESS_BAR_DURATION_MS / 100)) : 90);
    }, 100);
    
    dispatch(nextAction({
      key: searchParams.get('key'),
      data: {},
      success: (res) => {
        clearInterval(interval);
        setProgress(100);
        setTimeout(() => {
          setIsSubmitting(false);
          const nextUrl = get(res, 'data.url')
          if (nextUrl) navigate(nextUrl);
          showTitleAndFooter();
        }, 1000);
      },
      fail: (err) => {
        clearInterval(interval);
        handleFail(err);
      },
    }));
  };

  if (isSubmitting) {
    return <ProgressLoading className="mb-8" progress={progress}/>
  }

  return (
    <div className="bank-statement-container">
      {showDelayPopup && (
        <Modal
          title="This is taking a little longer than expected"
          onClose={() => setShowDelayPopup(false)}
        >
          <p className="text-sm text-gray-600 mb-2">
            We're still retrieving your transaction data. You don't need to stay on this page.
          </p>
          <p className="text-sm text-gray-600 mb-6">
            We'll notify you by text and email once you're ready to continue.
          </p>
          <Button
            className="w-full text-sm !py-3"
            label="Got it"
            modifier="primary"
            onClick={() => setShowDelayPopup(false)}
          />
        </Modal>
      )}

      <div className="my-8">
        <StatementsTable
          statements={accounts}
          isLoading={isLoading}
        />
      </div>

      {showUnmatchedConditionsPopup && (
        <Modal
          title="We need a bit more information"
          onClose={() => setShowUnmatchedConditionsPopup(false)}
        >
          <p className="text-sm text-gray-600 mb-6">
            We weren't able to retrieve enough account details to continue. To keep your application moving, please upload your bank statements for review.
          </p>
          <Button
            className="w-full text-sm !py-3"
            label="Upload Bank Statements"
            modifier="primary"
            isLoading={isRedirecting}
            onClick={goToUploadBankStatements}
          />
        </Modal>
      )}

      <GeneralError error={generalError} />

      <FormRow className="flex-col-reverse">
        <Button
          className="flex-1"
          modifier="outline"
          label="+ Link More"
          onClick={handleLinkMore}
          isLoading={isLoadingBack}
        />
        <Button
          className="flex-1"
          label="Next Step: Offer Selection"
          isLoading={isSubmitting}
          isDisabled={isLoading || !accounts || accounts.length === 0 || accounts.filter((acc) => acc.processingStatus === 'InProgress').length > 0}
          onClick={handleNext}
        />
      </FormRow>
    </div>
  );
};

ReviewBankAccounts.propTypes = {
  navigate: PropTypes.func.isRequired,
  dispatch: PropTypes.func.isRequired,
  searchParams: PropTypes.object.isRequired,
};

export default ReviewBankAccounts;
